import { useState } from 'react';
import { Alert, Button, Form, Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { normalizeFullWidthNumerics } from '../../../utils/numericInputNormalize';
import {
  TPP_MODAL_CONTENT_CLASS,
  TppBalanceDeltaHint,
  TppBtnSpinner,
  TppChargeAmountPresets,
  TppDateField,
  TppOptionalMark,
  TppRequiredMark,
  isTppPositiveAmountInputReady,
  selectTppInputOnFocus,
  tppTodayIso,
} from './tppUiShared';
import { useTppOnModalFreshOpen } from './useTppOnModalFreshOpen';
import type { TppChargeSubmitPayload } from './tppOpsModalTypes';

type Props = {
  show: boolean;
  onHide: () => void;
  closeLabel: string;
  money: (n: number) => string;
  balanceNumber: number | null;
  /** 貸出中でもチャージ可であることを明示 */
  isCheckedOut?: boolean;
  pending: boolean;
  onSubmit: (payload: TppChargeSubmitPayload) => void;
};

export default function TppChargeModal({
  show,
  onHide,
  closeLabel,
  money,
  balanceNumber,
  isCheckedOut = false,
  pending,
  onSubmit,
}: Props) {
  const { t } = useTranslation();
  const balance = balanceNumber ?? 0;
  const [outDate, setOutDate] = useState(() => tppTodayIso());
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');

  useTppOnModalFreshOpen(show, () => {
    setOutDate(tppTodayIso());
    setAmount('');
    setNote('');
  });

  const canSubmit = isTppPositiveAmountInputReady(amount);

  return (
    <Modal show={show} onHide={onHide} centered scrollable contentClassName={TPP_MODAL_CONTENT_CLASS}>
      <Form
        onSubmit={(e) => {
          e.preventDefault();
          if (!pending && canSubmit) {
            onSubmit({ outDate, amount, note });
          }
        }}
      >
        <Modal.Header closeButton closeLabel={closeLabel}>
          <Modal.Title>{t('tpp.charge_modal_title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {isCheckedOut ? (
            <Alert variant="info" className="small py-2 mb-3">
              {t('tpp.charge_while_checked_out_hint')}
            </Alert>
          ) : (
            <p className="small text-muted mb-3">
              {t('tpp.charge_help_hint')}
            </p>
          )}
          <TppDateField
            id="tpp-charge-date"
            label={t('tpp.charge_date')}
            value={outDate}
            onChange={setOutDate}
          />
          <Form.Group className="mb-2">
            <Form.Label htmlFor="tpp-charge-amount">
              {t('tpp.charge_amount')}
              <TppRequiredMark />
            </Form.Label>
            <TppChargeAmountPresets
              amount={amount}
              onPick={(n) => setAmount(String(n))}
              ariaLabel={t('tpp.charge_amount_presets')}
            />
            <Form.Control
              id="tpp-charge-amount"
              inputMode="decimal"
              value={amount}
              onChange={(e) => setAmount(normalizeFullWidthNumerics(e.target.value))}
              onFocus={selectTppInputOnFocus}
              className="tpp-amount-input"
              required
              autoFocus
              placeholder={t('tpp.charge_amount_placeholder')}
            />
            {balanceNumber != null && (
              <>
                <Form.Text muted className="d-block">
                  {t('tpp.charge_balance_current', { balance: money(balance) })}
                </Form.Text>
                <TppBalanceDeltaHint
                  mode="add"
                  amountRaw={amount}
                  balanceNumber={balance}
                  moneyLabel={money}
                  afterTemplate={(b) =>
                    t('tpp.charge_balance_after', { balance: b })
                  }
                />
              </>
            )}
          </Form.Group>
          <Form.Group className="mb-2">
            <Form.Label htmlFor="tpp-charge-note">
              {t('tpp.charge_note')}
              <TppOptionalMark />
            </Form.Label>
            <Form.Control
              id="tpp-charge-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button type="button" variant="secondary" onClick={onHide}>
            {t('common.cancel')}
          </Button>
          <Button type="submit" variant="primary" disabled={pending || !canSubmit}>
            <TppBtnSpinner show={pending} />
            {t('tpp.charge_confirm')}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}
